// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: luckyDrawPrizes/$prizeId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Prize inventory tracking for Danger Dan! lucky draw campaigns. Admin-managed with backend update access for decrementing remainingCount when prizes are won.
 */

export interface LuckyDrawPrizesRequest {
  prizeType: string;
  totalCount: number | TimeOperation | IncrementOperation | TokenAmount;
  remainingCount: number | TimeOperation | IncrementOperation | TokenAmount;
  label: string;
  campaignActive: boolean;
  imageUrl?: string;
  startDate?: number | TimeOperation | IncrementOperation | TokenAmount;
  endDate?: number | TimeOperation | IncrementOperation | TokenAmount;
  paused?: boolean;
}

export interface LuckyDrawPrizesResponse {
  prizeType: string;
  totalCount: number;
  remainingCount: number;
  label: string;
  campaignActive: boolean;
  imageUrl?: string;
  startDate?: number;
  endDate?: number;
  paused?: boolean;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a LuckyDrawPrizes operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildLuckyDrawPrizes(prizeId: string, data: LuckyDrawPrizesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `luckyDrawPrizes/${prizeId}`, document: doc };
}

/**
 * Admin, founder, or backend (PROJECT_VAULT_ADDRESS) can create a prize type with prizeType, totalCount, remainingCount (initially equal to totalCount), label display name, campaignActive flag, optional imageUrl, optional startDate/endDate (Unix seconds) for time-gated availability, and optional paused flag. All fields are mutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setLuckyDrawPrizes(prizeId: string, data: LuckyDrawPrizesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`luckyDrawPrizes/${prizeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting LuckyDrawPrizes: ${formatError(error)}`);
    return false;
  }
}

export type LuckyDrawPrizesRequestUpdate = Partial<LuckyDrawPrizesRequest>;

/**
 * Build a LuckyDrawPrizes update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateLuckyDrawPrizes(prizeId: string, data: LuckyDrawPrizesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `luckyDrawPrizes/${prizeId}`, document: doc };
}

/**
 * Admin, founder, or backend (PROJECT_VAULT_ADDRESS) can update any field. Backend decrements remainingCount when a prize is won. Admin/founder can toggle campaignActive, paused, adjust remainingCount, update label, totalCount, prizeType, startDate, endDate, or imageUrl as needed. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateLuckyDrawPrizes(prizeId: string, data: LuckyDrawPrizesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`luckyDrawPrizes/${prizeId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating LuckyDrawPrizes: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
   (Get Single Item)
 */
export async function getLuckyDrawPrizes(prizeId: string): Promise<LuckyDrawPrizesResponse | null> {
  try {
    const result = await get(`luckyDrawPrizes/${prizeId}`);
    return result as LuckyDrawPrizesResponse | null;
  } catch (error) {
    console.error(`Error getting LuckyDrawPrizes: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single LuckyDrawPrizes document. (
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
  )
 */
export function subscribeLuckyDrawPrizes(callback: (data: LuckyDrawPrizesResponse | null) => void, prizeId: string): Promise<() => Promise<void>> {
  return subscribe(`luckyDrawPrizes/${prizeId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as LuckyDrawPrizesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to LuckyDrawPrizes at path '${`luckyDrawPrizes/${prizeId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many LuckyDrawPrizes items from collection luckyDrawPrizes
 
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
  
 */
export async function getManyLuckyDrawPrizes(filter: string = ""): Promise<LuckyDrawPrizesResponse[]> {
  try {
    const results = await get("luckyDrawPrizes", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawPrizesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawPrizes collection at path 'luckyDrawPrizes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawPrizes collection at luckyDrawPrizes
 
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
  
 */
export function subscribeManyLuckyDrawPrizes(callback: (data: LuckyDrawPrizesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawPrizes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawPrizesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawPrizes collection at path '${"luckyDrawPrizes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all LuckyDrawPrizes items from collection luckyDrawPrizes
 
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
  
 */
export async function getAllLuckyDrawPrizes(filter: string = ""): Promise<LuckyDrawPrizesResponse[]> {
  try {
    const results = await get("luckyDrawPrizes", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawPrizesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawPrizes collection at path 'luckyDrawPrizes': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawPrizes collection at luckyDrawPrizes
 
  Read Operation Details: Anyone can read prize inventory. Frontend displays prize availability, campaign status, and scheduling info publicly.
  
 */
export function subscribeAllLuckyDrawPrizes(callback: (data: LuckyDrawPrizesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawPrizes", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawPrizesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawPrizes collection at path '${"luckyDrawPrizes"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count LuckyDrawPrizes items in collection luckyDrawPrizes.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countLuckyDrawPrizes(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("luckyDrawPrizes", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting LuckyDrawPrizes at path 'luckyDrawPrizes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on LuckyDrawPrizes items in collection luckyDrawPrizes.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateLuckyDrawPrizes(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("luckyDrawPrizes", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating LuckyDrawPrizes at path 'luckyDrawPrizes': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin, founder, or backend (PROJECT_VAULT_ADDRESS) can remove a prize type from the inventory.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteLuckyDrawPrizes(prizeId: string): Promise<boolean> {
  try {
    const succeeded = await set(`luckyDrawPrizes/${prizeId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting LuckyDrawPrizes: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for LuckyDrawPrizes for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteLuckyDrawPrizes(prizeId: string): DocumentOperation {
  return { path: `luckyDrawPrizes/${prizeId}`, document: null };
}

